import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";

import env from "env";

import {
  ApprovalTasksRequest,
  ApprovalTasksResponse,
  CreateProcessesRequest,
  CreateProcessesResponse,
  ApprovalTaskDto,
  ProcessCommentsResponse,
  ProcessCommentsRequest,
  CreateProcessCommentRequest,
  CreateProcessCommentResponse,
} from "./types";

const { approvalProcessorService } = env.api.baseUrl;
const baseQuery = fetchBaseQuery({
  baseUrl: approvalProcessorService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const approvalProcessorApi = createApi({
  reducerPath: "approvalProcessorApi",
  baseQuery: baseQueryCreator(baseQuery),
  endpoints: (builder) => ({
    getApprovalTasks: builder.query<
      ApprovalTasksResponse,
      ApprovalTasksRequest & {
        status?: string;
        processVariableText?: string;
        requesterId?: string;
        disbursementDate?: string;
      }
    >({
      query: ({ orgToken, ...params }) => ({
        url: "/approval-tasks",
        params: {
          ...params,
        },
        headers: {
          "org-token": orgToken,
        },
      }),
    }),
    getApprovalTask: builder.query<
      ApprovalTasksResponse,
      {
        referenceNumber: string;
        orgToken?: string;
      }
    >({
      query: ({ referenceNumber, orgToken }) => ({
        url: `/approval-tasks`,
        params: {
          pageNumber: 1,
          pageSize: 1,
          referenceNumber,
        },
        headers: {
          "org-token": orgToken,
        },
      }),
    }),
    updateApprovalTask: builder.mutation<{}, ApprovalTaskDto>({
      query: ({ id, orgToken, ...patch }) => ({
        url: `/approval-tasks/${id}`,
        method: "PUT",
        body: patch,
        headers: {
          "org-token": orgToken,
        },
      }),
    }),
    createProcesses: builder.mutation<
      CreateProcessesResponse,
      CreateProcessesRequest
    >({
      query: ({ orgToken, ...form }) => {
        return {
          url: `/processes`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "org-token": orgToken,
          },
          body: form,
        };
      },
    }),
    getProcessComments: builder.query<
      ProcessCommentsResponse,
      ProcessCommentsRequest
    >({
      query: ({ orgToken, processId, ...params }) => ({
        url: `/processes/${processId}/comments`,
        params: {
          ...params,
        },
        headers: {
          "org-token": orgToken,
        },
      }),
    }),
    createProcessComment: builder.mutation<
      CreateProcessCommentResponse,
      CreateProcessCommentRequest
    >({
      query: ({ approvalProcessId, orgToken, ...body }) => {
        return {
          url: `/processes/${approvalProcessId}/comments`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "org-token": orgToken,
          },
          body: body,
        };
      },
    }),
  }),
});

export const {
  useGetApprovalTasksQuery,
  useCreateProcessesMutation,
  useGetApprovalTaskQuery,
  useUpdateApprovalTaskMutation,
  useGetProcessCommentsQuery,
  useCreateProcessCommentMutation,
} = approvalProcessorApi;
